
import React, { useState, useRef } from 'react';
import { ChevronLeft, ChevronRight, X, Plus, Image, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { 
  Dialog, 
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';

interface Story {
  id: number;
  title: string;
  image: string;
  description?: string;
  views?: number;
  likes?: number;
  createdAt?: Date;
  expiresAt?: Date;
}

interface DoctorStoriesProps {
  stories: Story[];
  editable?: boolean;
  isDoctor?: boolean;
}

const DoctorStories: React.FC<DoctorStoriesProps> = ({ 
  stories: initialStories = [], 
  editable = false, 
  isDoctor = false 
}) => {
  const [stories, setStories] = useState<Story[]>(initialStories);
  const [selectedStory, setSelectedStory] = useState<Story | null>(null);
  const [storyIndex, setStoryIndex] = useState(0);
  const [showAddModal, setShowAddModal] = useState(false);
  const [caption, setCaption] = useState('');
  const [storyImage, setStoryImage] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [fileError, setFileError] = useState<string | null>(null);
  
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  const scroll = (direction: 'left' | 'right') => {
    if (scrollContainerRef.current) {
      const { current } = scrollContainerRef;
      const scrollAmount = 200;
      if (direction === 'left') {
        current.scrollLeft -= scrollAmount;
      } else {
        current.scrollLeft += scrollAmount;
      }
    }
  };
  
  const handleStoryClick = (story: Story, index: number) => {
    setSelectedStory(story);
    setStoryIndex(index);
  };
  
  const handleNextStory = () => {
    if (storyIndex < stories.length - 1) {
      setStoryIndex(storyIndex + 1);
      setSelectedStory(stories[storyIndex + 1]);
    } else {
      closeStoryView();
    }
  };
  
  const handlePrevStory = () => {
    if (storyIndex > 0) {
      setStoryIndex(storyIndex - 1);
      setSelectedStory(stories[storyIndex - 1]);
    }
  };
  
  const closeStoryView = () => {
    setSelectedStory(null);
  };
  
  const handleAddStoryClick = () => {
    // Trigger file input
    fileInputRef.current?.click();
  };
  
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    setFileError(null);
    
    if (file) {
      // Validate file size (5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        setFileError('File size exceeds 5MB limit');
        return;
      }
      
      // Validate file type (image or video)
      const fileType = file.type.split('/')[0];
      if (fileType !== 'image' && fileType !== 'video') {
        setFileError('Only image or video files are allowed');
        return;
      }
      
      // Read and preview the file
      const reader = new FileReader();
      reader.onloadend = () => {
        setStoryImage(reader.result as string);
        setShowAddModal(true);
      };
      reader.readAsDataURL(file);
    }
  };
  
  const handleAddStory = () => {
    if (!storyImage) {
      toast({
        title: "Missing image",
        description: "Please upload an image or video for your story.",
        variant: "destructive"
      });
      return;
    }
    
    if (!caption.trim()) {
      toast({
        title: "Missing caption",
        description: "Please add a caption for your story.",
        variant: "destructive"
      });
      return;
    }
    
    setIsUploading(true);
    
    // In a real app, this would be an API call to add the story
    setTimeout(() => {
      const newStory: Story = {
        id: Date.now(),
        title: caption.trim(),
        image: storyImage,
        description: caption.trim(),
        views: 0,
        likes: 0,
        createdAt: new Date(),
        expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000 * 7) // 7 days from now
      };
      
      setStories([newStory, ...stories]);
      resetAddStoryForm();
      
      toast({
        title: "Story added",
        description: "Your story has been successfully added and will be visible for 7 days.",
      });
    }, 1000);
  };
  
  const resetAddStoryForm = () => {
    setShowAddModal(false);
    setStoryImage(null);
    setCaption('');
    setIsUploading(false);
    setFileError(null);
  };
  
  // Handle swipe navigation for mobile
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);
  
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };
  
  const handleTouchEnd = (e: React.TouchEvent) => {
    touchEndX.current = e.changedTouches[0].clientX;
    handleSwipe();
  };
  
  const handleSwipe = () => {
    const swipeThreshold = 50;
    const difference = touchStartX.current - touchEndX.current;
    
    if (difference > swipeThreshold) {
      // Swipe left, go to next story
      handleNextStory();
    } else if (difference < -swipeThreshold) {
      // Swipe right, go to previous story
      handlePrevStory();
    }
  };
  
  if (stories.length === 0 && !editable) {
    return null;
  }

  return (
    <div className="mb-4 relative">
      <h3 className="font-semibold mb-3">Stories</h3>
      
      <div className="relative">
        <Button 
          variant="ghost" 
          size="icon" 
          className="absolute left-0 top-1/2 transform -translate-y-1/2 -translate-x-1/2 bg-background shadow-md h-8 w-8 rounded-full z-10 md:flex hidden"
          onClick={() => scroll('left')}
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        
        <div 
          ref={scrollContainerRef}
          className="flex overflow-x-auto gap-3 pb-2 scrollbar-hide"
          style={{ scrollBehavior: 'smooth' }}
        >
          {editable && (
            <div 
              className="flex-shrink-0 cursor-pointer group"
              onClick={handleAddStoryClick}
            >
              <div 
                className="h-20 w-20 rounded-full border-2 border-dashed border-muted-foreground/30 flex flex-col items-center justify-center hover:bg-muted/40 transition-colors"
              >
                <div className="h-8 w-8 rounded-full bg-primary flex items-center justify-center">
                  <span className="text-white text-lg font-semibold">+</span>
                </div>
                <span className="text-xs mt-1 text-muted-foreground">Add Story</span>
              </div>
              
              {/* Hidden file input */}
              <input
                type="file"
                ref={fileInputRef}
                className="hidden"
                accept="image/jpeg,image/png,video/mp4"
                onChange={handleFileChange}
              />
            </div>
          )}
          
          {stories.map((story, index) => (
            <div 
              key={story.id} 
              className="flex-shrink-0 cursor-pointer"
              onClick={() => handleStoryClick(story, index)}
            >
              <div className="h-20 w-20 rounded-full overflow-hidden relative border-2 border-primary">
                <img 
                  src={story.image} 
                  alt={story.title} 
                  className="h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black opacity-60"></div>
                <div className="absolute bottom-1 left-0 right-0">
                  <p className="text-white text-[10px] font-medium text-center truncate px-1">{story.title}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
        
        <Button 
          variant="ghost" 
          size="icon" 
          className="absolute right-0 top-1/2 transform -translate-y-1/2 translate-x-1/2 bg-background shadow-md h-8 w-8 rounded-full z-10 md:flex hidden"
          onClick={() => scroll('right')}
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
      
      {/* Story Viewer */}
      {selectedStory && (
        <div 
          className="fixed inset-0 bg-black z-50 flex items-center justify-center"
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          <button 
            className="absolute top-4 right-4 text-white p-2 z-10"
            onClick={closeStoryView}
          >
            <X className="h-6 w-6" />
          </button>
          
          <button 
            className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black/30 rounded-full p-2 text-white z-10"
            onClick={handlePrevStory}
            disabled={storyIndex === 0}
          >
            <ChevronLeft className="h-6 w-6" />
          </button>
          
          <div className="max-h-screen max-w-screen-md mx-auto relative">
            <img 
              src={selectedStory.image} 
              alt={selectedStory.title} 
              className="max-h-[calc(100vh-120px)] max-w-full object-contain"
            />
            
            {selectedStory.description && (
              <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/70 to-transparent">
                <p className="text-white">{selectedStory.description}</p>
              </div>
            )}
          </div>
          
          <button 
            className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black/30 rounded-full p-2 text-white z-10"
            onClick={handleNextStory}
            disabled={storyIndex === stories.length - 1}
          >
            <ChevronRight className="h-6 w-6" />
          </button>
        </div>
      )}
      
      {/* Add Story Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add New Story</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {storyImage && (
              <div className="w-full h-48 bg-muted rounded-md overflow-hidden">
                <img 
                  src={storyImage} 
                  alt="Story preview" 
                  className="w-full h-full object-contain"
                />
              </div>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="caption">Caption (100 chars max)</Label>
              <Textarea
                id="caption"
                placeholder="Add a caption to your story..."
                value={caption}
                onChange={(e) => setCaption(e.target.value.slice(0, 100))}
                maxLength={100}
              />
              <div className="text-xs text-right text-muted-foreground">
                {caption.length}/100 characters
              </div>
            </div>
            
            {fileError && (
              <div className="text-red-500 text-sm">
                {fileError}
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={resetAddStoryForm}>
              Cancel
            </Button>
            <Button 
              onClick={handleAddStory}
              disabled={isUploading || !caption.trim() || !storyImage}
            >
              {isUploading ? 'Adding...' : 'Add Story'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DoctorStories;
